import { Component } from '@theme/component';
import { requestIdleCallback } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';
/** @typedef {import('@shopify/events').ProductSelectEvent} ProductSelectEvent */

/**
 * Bump when the Storefront API GraphQL schema used below (`nodes`, `variants`,
 * `availableForSale`, `selectedOptions`) needs a newer version.
 */
const API_VERSION = '2025-07';

/**
 * How many variants one page asks for. Comfortably past what a real shoe carries
 * (a full size run across three fits is well under this), and the pagination below
 * covers the rest - the number is a query-cost lever, not a limit on the catalog.
 */
const VARIANTS_PAGE_SIZE = 100;

/**
 * How many siblings one request asks about. The Storefront API rejects a query
 * whose cost exceeds 1000 points, and cost here is roughly
 * `siblings x VARIANTS_PAGE_SIZE` - so asking about every colour at once fails
 * outright on any product with more than a handful of them, which is exactly the
 * product this component exists for. 5 x 100 leaves room for the rest of the
 * selection set inside that budget.
 */
const SIBLINGS_PER_QUERY = 5;

/**
 * Points every sibling colour swatch (separate products linked via the
 * `custom.siblings` metafield, rendered by snippets/product-siblings-swatches.liquid)
 * at the currently selected size and fit in that colour, and marks the colours that
 * don't carry it as unavailable. Unavailable swatches stay clickable - they just fall
 * back to that colour's own first available size and fit.
 *
 * "Size" here is the whole option value, fit code and all (`Adult | 40|W`), which is
 * what the Size radio carries as its value once assets/fit-switcher.js has pointed it
 * at a fit - so one lookup answers both "does this colour have my size and fit" and
 * "which variant is it".
 *
 * Colours are separate products here, not variants, so a colour's stock per size is
 * not part of the product Liquid renders; reaching it server-side would mean loading
 * every sibling product's variants on each PDP render. Instead this fetches full
 * variant availability for every sibling once, from the Storefront GraphQL API.
 *
 * Which colours exist is the metafield's job alone: this never adds, removes or
 * reorders swatches, it only annotates the ones Liquid rendered.
 *
 * Everything it matches on is language-sensitive - option values are translated per
 * storefront - so the API is asked for the page's own language, and a sibling whose sizes
 * still don't line up is left unmarked rather than dimmed (see `#sharesSizeVocabulary`).
 *
 * No-ops entirely when `data-storefront-token` is absent from the root element
 * (i.e. `settings.storefront_api_token` is blank) or when the product has no Size
 * option to check against, leaving the swatches as plain navigation links.
 */
export default class ProductSiblingsSwatches extends Component {
  /** @type {Element | null} */
  #section = null;

  /** @type {HTMLElement | null} */
  #variantPicker = null;

  /** @type {AbortController | undefined} */
  #abortController;

  /**
   * @type {Map<string, Map<string, string>> | undefined}
   * Product id -> normalized size option value -> the variant id selling it. Sold-out
   * combinations are left out entirely, so a missing entry is the "unavailable" signal.
   */
  #variantsByProductId;

  /** @type {string | undefined} A size selected before availability finished loading. */
  #pendingSizeValue;

  /** @type {string | undefined} The storefront's language as a Storefront API `LanguageCode`. */
  #languageCode;

  /** @type {boolean} Whether a size-vocabulary mismatch has already been reported. */
  #reportedSizeMismatch = false;

  connectedCallback() {
    super.connectedCallback();

    const { storefrontToken, sizeOptionName, languageCode } = this.dataset;
    if (!storefrontToken || !sizeOptionName) return;

    this.#languageCode = languageCode || undefined;

    this.#section = this.closest('.shopify-section, dialog');
    this.#variantPicker = this.closest('variant-picker');

    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.#abortController = new AbortController();
    // Off the critical path: nothing here changes what the page already shows, it only
    // dims colours the selected size isn't stocked in.
    requestIdleCallback(() => this.#fetchAvailability(storefrontToken, sizeOptionName));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * The `@inContext` pieces a query needs to be answered in the storefront's own language:
   * the extra variable declaration, the directive itself, and the variable to send with it.
   *
   * Option values are translatable, and Sebago's carry a translated age group ("Adult | 50|S"
   * reads "Adulto | 50|S" on the Italian storefront). The API answers in the shop's default
   * language unless a query asks otherwise, so without this the sizes it reports can't match
   * the ones Liquid rendered, every lookup in `#applyAvailability` misses, and every colour
   * reads as sold out.
   *
   * Returned as fragments rather than hardcoded because a storefront with no resolvable
   * language code has to send no directive at all: `$language` is non-null, so declaring it
   * without a value would have the API reject the query outright.
   * @returns {{ declaration: string, directive: string, variables: Record<string, string> }}
   */
  #languageContext() {
    if (!this.#languageCode) return { declaration: '', directive: '', variables: {} };

    return {
      declaration: ', $language: LanguageCode!',
      directive: ' @inContext(language: $language)',
      variables: { language: this.#languageCode },
    };
  }

  /**
   * Runs one Storefront GraphQL query, returning `data` or `undefined` if the request
   * failed or was aborted - callers treat a missing result as "leave the swatches as
   * plain links", which is the same thing that happens without an API token at all.
   *
   * GraphQL reports its own failures - a throttled query, one over the cost ceiling,
   * a token without product read access - inside a 200 OK body, so the `errors` array
   * is checked as well as the HTTP status. Without that the whole feature degrades
   * silently and looks like a product that simply has nothing in stock.
   * @param {string} token
   * @param {string} query
   * @param {Record<string, unknown>} variables
   * @returns {Promise<any | undefined>}
   */
  async #query(token, query, variables) {
    try {
      const response = await fetch(`/api/${API_VERSION}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query, variables }),
        signal: this.#abortController?.signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const payload = await response.json();
      if (Array.isArray(payload?.errors) && payload.errors.length) {
        throw new Error(payload.errors.map((/** @type {any} */ error) => error?.message).join('; '));
      }

      return payload?.data;
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[product-siblings-swatches] Storefront API request failed', error);
      }
      return undefined;
    }
  }

  /**
   * Fetches variant availability for every sibling swatch and applies it to whatever
   * size is currently selected (or is selected while the requests are still in
   * flight). Split across as many requests as the query-cost ceiling needs (see
   * `SIBLINGS_PER_QUERY`); they run concurrently and none of them blocks the page.
   * @param {string} token
   * @param {string} sizeOptionName
   */
  async #fetchAvailability(token, sizeOptionName) {
    const swatches = Array.from(this.querySelectorAll('[data-sibling-swatch][data-product-id]'));
    if (!swatches.length) return;

    const ids = swatches.map((swatch) => `gid://shopify/Product/${swatch.getAttribute('data-product-id')}`);

    const { declaration, directive, variables } = this.#languageContext();

    const query = `
      query SiblingSwatchAvailability($ids: [ID!]!${declaration})${directive} {
        nodes(ids: $ids) {
          ... on Product {
            variants(first: ${VARIANTS_PAGE_SIZE}) {
              pageInfo {
                hasNextPage
                endCursor
              }
              nodes {
                id
                availableForSale
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
        }
      }
    `;

    /** @type {string[][]} */
    const idBatches = [];
    for (let index = 0; index < ids.length; index += SIBLINGS_PER_QUERY) {
      idBatches.push(ids.slice(index, index + SIBLINGS_PER_QUERY));
    }

    const answers = await Promise.all(
      idBatches.map((batch) => this.#query(token, query, { ids: batch, ...variables })),
    );

    // Flattened back into one list positioned like `swatches`: `nodes(ids:)` answers
    // in request order, so a sibling's variants sit at its own index. A batch that
    // failed contributes holes, which the loop below skips rather than reading as
    // "sold out" (see the `!connection` guard).
    /** @type {any[]} */
    const nodes = [];
    idBatches.forEach((batch, batchIndex) => {
      const batchNodes = answers[batchIndex]?.nodes;
      for (let index = 0; index < batch.length; index++) {
        nodes.push(Array.isArray(batchNodes) ? batchNodes[index] : undefined);
      }
    });

    if (!nodes.some(Boolean)) return;

    /** @type {Map<string, Map<string, string>>} */
    const variantsByProductId = new Map();

    await Promise.all(
      swatches.map(async (swatch, index) => {
        const productId = swatch.getAttribute('data-product-id');
        if (!productId) return;

        // No variants back for this sibling - the id resolved to nothing, or the
        // product isn't published to the sales channel this token belongs to. An
        // entry here means "these are the sizes it sells", so a missing answer must
        // stay missing: recording an empty map would read as "sold out in every
        // size" and dim a colour that may be perfectly in stock.
        const connection = nodes[index]?.variants;
        if (!connection) return;

        const variants = [...(connection.nodes ?? [])];

        // Past the first page only for the rare product stocked in more sizes than one
        // request returns - without this its last sizes would read as sold out.
        let pageInfo = connection.pageInfo;
        while (pageInfo?.hasNextPage && pageInfo.endCursor) {
          const nextPage = await this.#fetchVariantsPage(token, ids[index] ?? '', pageInfo.endCursor);
          // Same reasoning as above, one page in: a half-read product would report
          // its unread sizes as sold out, so it is dropped rather than guessed at.
          if (!nextPage) return;
          variants.push(...nextPage.nodes);
          pageInfo = nextPage.pageInfo;
        }

        variantsByProductId.set(productId, this.#collectSizeVariants(variants, sizeOptionName));
      }),
    );

    this.#variantsByProductId = variantsByProductId;

    if (this.#pendingSizeValue !== undefined) {
      this.#applyAvailability(this.#pendingSizeValue);
      return;
    }

    const checkedSizeInput = this.#variantPicker?.querySelector(
      `fieldset input[data-option-name="${CSS.escape(sizeOptionName)}"]:checked`,
    );
    if (checkedSizeInput instanceof HTMLInputElement) {
      this.#applyAvailability(checkedSizeInput.value);
    }
  }

  /**
   * Fetches one further page of a single sibling's variants.
   * @param {string} token
   * @param {string} id - The sibling's product GID.
   * @param {string} cursor
   * @returns {Promise<{ nodes: any[], pageInfo: { hasNextPage: boolean, endCursor: string | null } } | undefined>}
   */
  async #fetchVariantsPage(token, id, cursor) {
    const { declaration, directive, variables } = this.#languageContext();

    const query = `
      query SiblingSwatchVariantsPage($id: ID!, $cursor: String!${declaration})${directive} {
        node(id: $id) {
          ... on Product {
            variants(first: ${VARIANTS_PAGE_SIZE}, after: $cursor) {
              pageInfo {
                hasNextPage
                endCursor
              }
              nodes {
                id
                availableForSale
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
        }
      }
    `;

    return (await this.#query(token, query, { id, cursor, ...variables }))?.node?.variants;
  }

  /**
   * Reduces a sibling's variants to the size values it can currently sell, each
   * mapped to the variant id that sells it.
   * @param {any[]} variants
   * @param {string} sizeOptionName
   * @returns {Map<string, string>}
   */
  #collectSizeVariants(variants, sizeOptionName) {
    const sizeOptionNameLower = sizeOptionName.toLowerCase();
    /** @type {Map<string, string>} */
    const sizeVariants = new Map();

    for (const variant of variants) {
      if (!variant.availableForSale) continue;
      const sizeOption = variant.selectedOptions?.find(
        (/** @type {{ name: string, value: string }} */ option) => option.name.toLowerCase() === sizeOptionNameLower,
      );
      if (!sizeOption) continue;

      const size = this.#normalizeSize(sizeOption.value);
      const variantId = String(variant.id ?? '')
        .split('/')
        .pop();
      if (variantId && !sizeVariants.has(size)) sizeVariants.set(size, variantId);
    }

    return sizeVariants;
  }

  /**
   * Reads the checked Size input directly from the DOM rather than trusting
   * the event payload's `selectedOptions`/`product` fields, since this is the
   * only consumer in the codebase relying on those fields and every other
   * signal here (variant-picker instance identity, current selection) is
   * already available straight from the live DOM.
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (this.#variantPicker && event.target !== this.#variantPicker) return;

    const sizeOptionName = this.dataset.sizeOptionName;
    if (!sizeOptionName) return;

    const checkedSizeInput = this.#variantPicker?.querySelector(
      `fieldset input[data-option-name="${CSS.escape(sizeOptionName)}"]:checked`,
    );
    if (!(checkedSizeInput instanceof HTMLInputElement)) return;

    if (!this.#variantsByProductId) {
      this.#pendingSizeValue = checkedSizeInput.value;
      return;
    }

    this.#applyAvailability(checkedSizeInput.value);
  };

  /**
   * Points every sibling swatch at `sizeValue` in its own colour, and marks the ones
   * that can't sell it as unavailable. The swatch for the product currently being
   * viewed is left alone - navigating to itself is moot, and its own Size fieldset
   * already reflects this directly.
   *
   * Unavailable swatches stay fully clickable: their link drops back to the bare
   * product URL, which Shopify resolves to that colour's first available size and fit.
   * That is why the state is carried in the accessible *name* rather than in
   * `aria-disabled` - the link still works, and marking it disabled would tell
   * assistive tech the opposite, taking a working route to the colour away from
   * exactly the shoppers who can't see that it is only dimmed.
   * @param {string} sizeValue
   */
  #applyAvailability(sizeValue) {
    if (!this.#variantsByProductId) return;

    const normalizedSize = this.#normalizeSize(sizeValue);
    const unavailableLabel = this.dataset.unavailableLabel;
    const ownSizes = this.#ownSizeValues();

    for (const swatch of this.querySelectorAll('[data-sibling-swatch][data-product-id]')) {
      if (!(swatch instanceof HTMLElement) || swatch.classList.contains('is-selected')) continue;

      const productId = swatch.getAttribute('data-product-id') ?? '';
      const sizeVariants = this.#variantsByProductId.get(productId);
      const variantId = sizeVariants?.get(normalizedSize);
      const isUnavailable =
        sizeVariants !== undefined &&
        variantId === undefined &&
        this.#sharesSizeVocabulary(sizeVariants, ownSizes, productId);

      // `data-product-url` stays the pristine URL Liquid rendered, so every pass
      // rebuilds the link from it rather than from the previous size's answer.
      const productUrl = swatch.dataset.productUrl;
      if (productUrl) {
        swatch.setAttribute('href', variantId ? `${productUrl}?variant=${variantId}` : productUrl);
      }

      swatch.classList.toggle('is-unavailable', isUnavailable);

      // Rebuilt from `data-swatch-label` rather than edited in place, so a colour
      // that goes unavailable and back doesn't end up with the suffix twice.
      const swatchLabel = swatch.dataset.swatchLabel;
      if (swatchLabel) {
        swatch.setAttribute(
          'aria-label',
          isUnavailable && unavailableLabel ? `${swatchLabel} - ${unavailableLabel}` : swatchLabel,
        );
      }
    }
  }

  /**
   * Every size the product on the page offers, normalized the way the sibling maps are -
   * the yardstick `#sharesSizeVocabulary` measures a sibling's answer against.
   * @returns {Set<string>}
   */
  #ownSizeValues() {
    const sizeOptionName = this.dataset.sizeOptionName;
    if (!sizeOptionName) return new Set();

    const inputs = this.#variantPicker?.querySelectorAll(
      `fieldset input[data-option-name="${CSS.escape(sizeOptionName)}"]`,
    );

    return new Set(
      Array.from(inputs ?? [], (input) => this.#normalizeSize(/** @type {HTMLInputElement} */ (input).value)),
    );
  }

  /**
   * Whether a sibling's sizes and the page's own are written in the same vocabulary - that
   * is, whether "this colour doesn't stock that size" is a conclusion the data can actually
   * support.
   *
   * Sharing nothing at all is not a colour sold out in every size, it's two lists that
   * aren't describing sizes the same way: in practice the API answered in a different
   * language than Liquid rendered, which is what happens when a sibling is missing this
   * storefront's option value translations and falls back to the shop's default. Dimming on
   * that evidence would mark every colour unavailable - the exact failure this guard exists
   * to stop - so the swatch is left neutral and the mismatch is reported instead of being
   * absorbed silently.
   *
   * An empty map is left alone: that one really is a colour with nothing available.
   * @param {Map<string, string>} sizeVariants
   * @param {Set<string>} ownSizes
   * @param {string} productId
   * @returns {boolean}
   */
  #sharesSizeVocabulary(sizeVariants, ownSizes, productId) {
    if (!sizeVariants.size || !ownSizes.size) return true;
    if ([...sizeVariants.keys()].some((size) => ownSizes.has(size))) return true;

    if (!this.#reportedSizeMismatch) {
      this.#reportedSizeMismatch = true;
      console.warn(
        `[product-siblings-swatches] Sibling ${productId} reports sizes sharing nothing with this page's, so its ` +
          'swatches are left unmarked rather than dimmed. The Storefront API most likely answered in a different ' +
          `language than the page - check that sibling's option value translations.`,
        { sibling: [...sizeVariants.keys()], page: [...ownSizes] },
      );
    }

    return false;
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #normalizeSize(value) {
    return value.trim().toLowerCase();
  }
}

if (!customElements.get('product-siblings-swatches')) {
  customElements.define('product-siblings-swatches', ProductSiblingsSwatches);
}
